import { FrameHeader } from "./FrameHeader";
import { read_u8, skip_bytes, Sink } from "../../helpers/serializerHelpers";
import { InferenceTrackState } from "./InferenceTrackState";
import { InferredObjectTlvInfo } from "./InferredObjectTlvInfo";
import { OrientationData } from "./OrientationData";
import { PointCloudCartesian } from "./PointCloudCartesian";
import { PointCloudSideInfo } from "./PointCloudSideInfo";
import { PointCloudSpherical } from "./PointCloudSpherical";
import { TemperatureData } from "./TemperatureData";
import { TlvHeader } from "./TlvHeader";
import { TrackState } from "./TrackState";

export enum RecordType {
    Unknown = 0,
    FrameStats = 4,
    Orientation = 5,
    PointCloud = 6,
    RadarTarget = 7,
    RadarTargetIndex = 8,
    PointCloudSideData = 9,
    Temperature = 10,
    StaticPointCloud = 11,
    StaticPointCloudSideData = 12,
    CameraTarget = 13,
    InferredObject = 14
};

class AngleAndRange {
    angle: number = 0;
    range: number = 0;
}

export class Tlv {

    private static inferredObjectLength: number = new InferredObjectTlvInfo().getLength();
    private static trackStateLength: number = new TrackState().getLength();
    private static inferenceTrackStateLength: number = new InferenceTrackState().getLength();
    private static sideInfoLength: number = new PointCloudSideInfo().getLength();
    private static sphericalPointLength: number = new PointCloudSpherical().getLength();
    private static cartesianPointLength: number = new PointCloudCartesian().getLength();
    private static maxTlvSize: number = 1000 * this.sphericalPointLength;

    public header: TlvHeader = new TlvHeader();

    public getDataType(): RecordType { return this.header ? this.header?.objectType : RecordType.Unknown; }

    public getTotalLength() { return (this.header?.dataLength ?? 0) + (this.header?.getLength() ?? 0); }
    public getDataLength() { return this.header?.dataLength ?? 0; }

    public targetIndices: number[] = []; //bytes (uint8)
    public inferredObjects: InferredObjectTlvInfo[] = [];
    public radarTargets: TrackState[] = [];
    public cameraTargets: TrackState[] = [];
    public cameraTargetsV2: InferenceTrackState[] = [];
    public pointClouds: PointCloudSpherical[] = [];
    public pointCloudSideInfos: PointCloudSideInfo[] = [];
    public staticPointClouds: PointCloudCartesian[] = [];
    public staticPointCloudSideInfos: PointCloudSideInfo[] = [];
    public temperatures: TemperatureData = new TemperatureData();

    public clutterPoints: AngleAndRange[] = [];
    public orientation: OrientationData = new OrientationData();

    public parseRadarTargetIndices(bytes: Sink) {
        var numIndices = this.getDataLength();
        //console.log(`Parsing {numIndices} target indices`);
        this.targetIndices = new Array<number>(numIndices);
        for (var i = 0; i < numIndices; i++) {
            this.targetIndices[i] = read_u8(bytes);
        }
    }

    public parseInferredObjects(bytes: Sink) {
        //InferredObject TLVs
        var numTargets = this.getDataLength() / Tlv.inferredObjectLength;
        //console.log(`Parsing ${numTargets} targets`);

        var inferredObjects = new Array<InferredObjectTlvInfo>(numTargets);
        for (var tIndex = 0; tIndex < numTargets; tIndex++) {
            inferredObjects[tIndex] = new InferredObjectTlvInfo();
            inferredObjects[tIndex].deserialize(bytes);
        }

        return inferredObjects;
    }

    public parseInferenceTrackStates(bytes: Sink) {
        //TargetState LTVs
        var numTargets = this.getDataLength() / Tlv.inferenceTrackStateLength;
        //console.log(`Parsing ${numTargets} targets`);

        var trackStates = new Array<InferenceTrackState>(numTargets);
        for (var tIndex = 0; tIndex < numTargets; tIndex++) {
            trackStates[tIndex] = new InferenceTrackState();
            trackStates[tIndex].deserialize(bytes);
        }

        return trackStates;
    }

    public parseTrackStates(bytes: Sink) {
        //TargetState LTVs
        var numTargets = this.getDataLength() / Tlv.trackStateLength;
        //console.log(`Parsing ${numTargets} targets`);

        var trackStates = new Array<TrackState>(numTargets);
        for (var tIndex = 0; tIndex < numTargets; tIndex++) {
            trackStates[tIndex] = new TrackState();
            trackStates[tIndex].deserialize(bytes);
        }

        return trackStates;
    }

    public parsePointCloudSideInfos(bytes: Sink) {
        //PointCloudSideInfo TLV
        var numPoints = Math.floor(this.getDataLength() / Tlv.sideInfoLength);
        var sideInfoArray = new Array<PointCloudSideInfo>(numPoints);
        //console.log(`Parsing {numPoints} side points`);

        if (numPoints > 0) {
            for (var i = 0; i < numPoints; i++) {
                sideInfoArray[i] = new PointCloudSideInfo();
                sideInfoArray[i].deserialize(bytes);
            }
        }

        return sideInfoArray;
    }

    public parseTemperature(bytes: Sink) {
        //Temperature TLV
        this.temperatures.deserialize(bytes);
    }

    public parseOrientation(bytes: Sink) {
        //Orientation TLV
        this.orientation.deserialize(bytes);
    }

    public parsePointClouds(bytes: Sink) {
        //Point cloud TLV
        var offset = 0;
        var numInputPoints = this.getDataLength() / Tlv.sphericalPointLength;
        //console.log(`Parsing {numInputPoints} points`);

        if (numInputPoints > 0) {
            //Read all point cloud data
            var newClutterPoints: AngleAndRange[] = [];
            this.pointClouds = new Array<PointCloudSpherical>(numInputPoints);

            for (var pIndex = 0; pIndex < numInputPoints; pIndex++) {
                var pc = this.pointClouds[pIndex] = new PointCloudSpherical();
                pc.deserialize(bytes);
                //pc.Angle *= (float)Math.PI / 180.0f;
                pc.z = pc.range * Math.sin(pc.elevation);
                var r = pc.range * Math.cos(pc.elevation);
                pc.x = (r * Math.sin(pc.angle));
                pc.y = (r * Math.cos(pc.angle));

                //console.log(`Converting tlv spherical (r: ${pc.range}, a: ${pc.angle}, e: ${pc.elevation}) -> (${pc.x}, ${pc.y})`);


                pc.isInRange = pc.range > 1 && pc.range < 6 && //Check range to front and back walls
                    (pc.angle > -50.0 * Math.PI / 180.0) && pc.angle < (50.0 * Math.PI / 180.0) && // Check FOV params
                    pc.x > 0 && pc.x < 0 + 99999 && //Check X is in our range
                    pc.y > 0 && pc.y < 0 + 99999; //Check Y is in our range

                //Clutter removal
                pc.isStatic = Math.abs(pc.doppler) < .0001;
                pc.isClutter = pc.isStatic && this.clutterPoints.findIndex(ar => ar.angle === pc.angle && ar.range === pc.range) >= 0;
                if (pc.isStatic)
                    newClutterPoints.push({ angle: pc.angle, range: pc.range });

                this.clutterPoints = newClutterPoints;
            }
        }
    }

    public parseStaticPointClouds(bytes: Sink) {
        // Static point cloud TLV
        var numInputPoints = this.getDataLength() / Tlv.cartesianPointLength;

        if (numInputPoints > 0) {
            // Read all static point cloud data
            this.staticPointClouds = new Array<PointCloudCartesian>(numInputPoints);

            for (var pIndex = 0; pIndex < numInputPoints; pIndex++) {
                this.staticPointClouds[pIndex] = new PointCloudCartesian().deserialize(bytes);
            }
        }
    }

    public deserialize(frameHeader: FrameHeader, bytes: Sink) {
        this.header.deserialize(bytes);

        if (this.header.dataLength > Tlv.maxTlvSize) {
            throw new Error("Invalid TLV DataLength: " + this.header.dataLength + " (Max: " + Tlv.maxTlvSize + ")");
        } else if (this.header.dataLength > bytes.view.byteLength - bytes.pos) {
            return this;
        }

        //Now load the data
        if (this.getDataType() == RecordType.RadarTargetIndex)
            this.parseRadarTargetIndices(bytes);
        else if (this.getDataType() == RecordType.InferredObject)
            this.inferredObjects = this.parseInferredObjects(bytes);
        else if (this.getDataType() == RecordType.RadarTarget)
            this.radarTargets = this.parseTrackStates(bytes);
        else if (this.getDataType() == RecordType.CameraTarget) {
            if (frameHeader.version == 2 && frameHeader.platform == 1) {
                this.cameraTargetsV2 = this.parseInferenceTrackStates(bytes);
            }
            else
                this.cameraTargets = this.parseTrackStates(bytes);
        }
        else if (this.getDataType() == RecordType.PointCloud)
            this.parsePointClouds(bytes);
        else if (this.getDataType() == RecordType.PointCloudSideData)
            this.pointCloudSideInfos = this.parsePointCloudSideInfos(bytes);
        else if (this.getDataType() == RecordType.Temperature)
            this.parseTemperature(bytes);
        else if (this.getDataType() == RecordType.Orientation)
            this.parseOrientation(bytes);
        else if (this.getDataType() == RecordType.StaticPointCloud)
            this.parseStaticPointClouds(bytes);
        else if (this.getDataType() == RecordType.StaticPointCloudSideData)
            this.staticPointCloudSideInfos = this.parsePointCloudSideInfos(bytes);
        else {
            console.warn(`Unknown TLV data type: ${this.header.objectType}, skipping ${this.header.dataLength} bytes`);
            skip_bytes(bytes, this.header.dataLength);
        }
        return this;
    }

}


